<template>
  <div
    class="onboarding-questionnaire"
    :class="{
      'onboarding-questionnaire--16167':
        netNewQuestionnaire16167Variant() === 'B',
    }"
  >
    <Transition name="fade" mode="out-in">
      <div
        :key="`${view.currentSectionIndex !== 0}`"
        class="onboarding-questionnaire__header"
      >
        <Stepper v-if="view.currentSectionIndex !== 0" :view="view" />
        <Logo
          v-else-if="view.isLogoVisible"
          class="onboarding-questionnaire__logo"
          :withText="true"
        />
      </div>
    </Transition>

    <Transition name="fade" mode="out-in">
      <component
        :is="currentPageComponent"
        :key="view.currentPage.id"
        :view="view"
      />
    </Transition>

    <Transition name="button-fade" mode="out-in">
      <div
        v-if="view.isContinueButtonVisible"
        class="onboarding-questionnaire__button-wrapper"
      >
        <AppButton
          class="onboarding-questionnaire__button"
          @click="view.continue()"
          >Continue</AppButton
        >
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import { QuestionnaireView } from '@/models/onboarding'
import { onboardingPages } from '@/models/onboarding.pages'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import { AuthUser } from '@/models/interfaces.auth'
import { netNewQuestionnaire16167Variant } from '@/services/ab'

import Logo from '@/components/ui/Logo.vue'
import AppButton from '@/app/components/button/AppButton.vue'
import Stepper from '@/app/components/onboarding/Stepper.vue'
import Text from '@/app/components/onboarding/questionnaire/Text.vue'
import Options from '@/app/components/onboarding/questionnaire/Options.vue'
import FinishingSetup from '@/app/components/onboarding/questionnaire/FinishingSetup.vue'
import Review from '@/app/components/onboarding/questionnaire/Review.vue'
import Statements from '@/app/components/onboarding/questionnaire/Statements.vue'
import Books from '@/app/components/onboarding/questionnaire/Books.vue'
import DailyGoal from '@/app/components/onboarding/questionnaire/DailyGoal.vue'
import Notifications from '@/app/components/onboarding/questionnaire/Notifications.vue'
import CommitmentPact from '@/app/components/onboarding/questionnaire/CommitmentPact.vue'
import Welcome16167 from '@/app/components/onboarding/questionnaire/Welcome16167.vue'
import Splitscreen16167 from '@/app/components/onboarding/questionnaire/Splitscreen16167.vue'
import Upgrade16167 from '@/app/components/onboarding/questionnaire/Upgrade16167.vue'
import GenreSelection16167 from '@/app/components/onboarding/questionnaire/GenreSelection16167.vue'
import BookSelection16167 from '@/app/components/onboarding/questionnaire/BookSelection16167.vue'
import Loading16167 from '@/app/components/onboarding/questionnaire/Loading16167.vue'
import Summary16167 from '@/app/components/onboarding/questionnaire/Summary16167.vue'
import MillionUsers16167 from '@/app/components/onboarding/questionnaire/MillionUsers16167.vue'
import ProductDemo16167 from '@/app/components/onboarding/questionnaire/ProductDemo16167.vue'

const props = defineProps<{ nextOnboardingPage: () => void }>()

const pageComponents = {
  Text,
  Options,
  FinishingSetup,
  Review,
  Statements,
  Books,
  DailyGoal,
  Notifications,
  CommitmentPact,
  Welcome16167,
  Splitscreen16167,
  Upgrade16167,
  GenreSelection16167,
  BookSelection16167,
  Loading16167,
  Summary16167,
  MillionUsers16167,
  ProductDemo16167,
}

const currentPageComponent = computed(() => {
  return pageComponents[view.value.currentPage.type]
})

const user = JSON.parse(localStorage.getItem('user') || '{}') as AuthUser
const firstName = user.full_name?.match(/^[a-zA-ZÀ-ÿ]+/)?.[0]

const view = ref(
  new QuestionnaireView(
    onboardingPages.questionnaire,
    props.nextOnboardingPage,
    firstName,
  ),
)

onMounted(async () => {
  // Preload cache data while the user goes through the questionnaire.
  // We run it without `await` on purpose so that it doesn't block the
  // loading of the rest of the page.
  preloadCacheData()
})

async function preloadCacheData(): Promise<void> {
  try {
    await Promise.all([
      backend.getBooks(),
      backend.getArticles(),
      backend.getPodcasts(),
      backend.getBookCollections(),
      backend.getRecommendations(),
      backend.getExcerpt(),
      backend.getBookOfTheDay(),
      backend.getUserDocs(),
      backend.getBookTags(),
    ])
  } catch (error: any) {
    Sentry.captureException(error)
  }
}
</script>

<style lang="scss" scoped>
:global(#app) {
  overflow: auto;
}

.onboarding-questionnaire {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  @media (min-width: $w-medium-b) and (min-height: 900px) {
    --onboarding-padding: 48px;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 0;
    z-index: 99;
  }

  &__logo {
    display: block;
    height: 24px;
    margin-top: 22px;
    animation: fadeIn 1s forwards;
    @supports (padding-top: env(safe-area-inset-top)) {
      margin-top: max(22px, env(safe-area-inset-top));
    }
  }

  &__button-wrapper {
    position: relative;
    display: flex;
    justify-content: center;
    width: 100%;
    padding: var(--onboarding-padding);
    padding-top: 0;

    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      z-index: 0;
      display: block;
      width: 100%;
      height: calc(100% + var(--onboarding-padding));
      background: $c-bg;
      mask: linear-gradient(
        0deg,
        rgba(0, 0, 0, 1) 0%,
        rgba(0, 0, 0, 1) 65%,
        rgba(0, 0, 0, 0) 100%
      );
    }
  }

  &__button {
    z-index: 1;
    width: 100%;
    margin-top: calc(var(--onboarding-padding) * -1);
    max-width: var(--onboarding-inner-width);
    height: 56px;
    font-size: 18px;
  }
}

.onboarding-questionnaire--16167 {
  .onboarding-questionnaire__header {
    min-height: 46px;
  }

  :deep(h1 span:not([class])),
  :deep(h2 span:not([class])) {
    background: transparent;
    padding-inline: 0;
    padding-block: 0;
    color: #5aa96b;
  }
}

.fade {
  &-enter-active,
  &-leave-active {
    // Prevents clicking buttons during the transition.
    pointer-events: none;
  }
}

.button-fade {
  &-enter-active,
  &-leave-active {
    pointer-events: none;
    transition: all 0.3s ease;
  }

  &-enter-active {
    // Prevents layout shifts from the button component disappearing.
    position: absolute;
    left: 0;
    bottom: 0;
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;
  }
}
</style>
